import { RoundedButton, SubmitButtonTheme } from '@sparelabs/nutshell-legacy'
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber'
import React from 'react'
import { useAsync } from 'react-use'
import { ApiClientBuilder } from 'src/api/ApiClientBuilders'
import {
  ActionButtonsWrapper,
  ErrorView,
  FillWidthButtonsWrapper,
  FormContainer,
  InfoDescription,
  InfoTitle,
  LoadingView,
} from 'src/components'
import { LinkText } from 'src/components/LinkText'
import { AuthenticationHelper } from 'src/helpers/AuthenticationHelper'
import { st } from 'src/locales/TranslationHelper'
import { useFormContext } from '../FormContext'
import { FormStep } from '../FormStepTypes'

const phoneUtil = PhoneNumberUtil.getInstance()
interface IRiderProfileDedupeViewProps {
  firstName: string
  lastName: string
  showWrongAccount: boolean
  loginMethod: string
  loginCredential: string
  organizationName: string
  onNotMe: () => void
  onContinue: () => void
  onBack: () => void
}

const RiderProfileDedupeView = ({
  firstName,
  lastName,
  showWrongAccount,
  loginMethod,
  loginCredential,
  organizationName,
  onNotMe,
  onContinue,
  onBack,
}: IRiderProfileDedupeViewProps): JSX.Element => {
  const fullName = `${firstName} ${lastName}`
  const { setShowEmailLoginFirst } = useFormContext()

  const onBackToPhoneAuth = () => {
    setShowEmailLoginFirst(false)
    onBack()
  }

  const onBackToEmailAuth = () => {
    setShowEmailLoginFirst(true)
    onBack()
  }

  if (showWrongAccount) {
    return (
      <FormContainer>
        <InfoTitle>{st.forms.riderProfileDedupe.notMeTitle()}</InfoTitle>
        <InfoDescription>
          {st.forms.riderProfileDedupe.notMeDescriptionIdentity({ loginMethod, loginCredential, fullName })}
        </InfoDescription>
        <InfoDescription>
          {st.forms.riderProfileDedupe.notMeDescriptionAlternativePart1()}
          <LinkText onClick={onBackToPhoneAuth}>{st.forms.riderProfileDedupe.notMePhoneLink()}</LinkText>
          {st.forms.riderProfileDedupe.notMeDescriptionAlternativePart2()}
          <LinkText onClick={onBackToEmailAuth}>{st.forms.riderProfileDedupe.notMeEmailLink()}</LinkText>
        </InfoDescription>
        <InfoDescription>{st.forms.riderProfileDedupe.notMeDescriptionContact({ organizationName })}</InfoDescription>
        <ActionButtonsWrapper>
          <RoundedButton onClick={onBack}>{st.common.back()}</RoundedButton>
        </ActionButtonsWrapper>
      </FormContainer>
    )
  }

  return (
    <FormContainer>
      <InfoTitle>{st.forms.riderProfileDedupe.welcomeMessage({ fullName })}</InfoTitle>
      <InfoDescription>{st.forms.riderProfileDedupe.infoPrompt()}</InfoDescription>
      <FillWidthButtonsWrapper>
        <RoundedButton buttonTheme={SubmitButtonTheme} onClick={onContinue}>
          {st.common.continue()}
        </RoundedButton>
        <RoundedButton onClick={onNotMe}>{st.forms.riderProfileDedupe.notMeButton()}</RoundedButton>
      </FillWidthButtonsWrapper>
    </FormContainer>
  )
}

export const RiderProfileDedupeStep = (): JSX.Element => {
  const { onNextStep, goToStep, organization } = useFormContext()
  const [showWrongAccount, setShowWrongAccount] = React.useState(false)

  const {
    loading,
    error,
    value: rider,
  } = useAsync(async () => {
    const client = ApiClientBuilder.build()
    return client.users.getMeRider()
  }, [])

  // Add this effect to handle incomplete profile redirect
  React.useEffect(() => {
    if (rider && (!rider.firstName || !rider.lastName)) {
      onNextStep()
    }
  }, [rider, onNextStep])

  const handleBack = () => {
    AuthenticationHelper.clear()
    goToStep(FormStep.RiderAuth)
  }

  if (loading) {
    return <LoadingView />
  }

  if (error || !rider) {
    return <ErrorView message={error?.message || st.errors.noContent()} />
  }

  // If the profile is incomplete (no name), skip straight to the next step (handled by the effect above)
  if (!rider.firstName || !rider.lastName) {
    return <LoadingView />
  }

  // Determine login method based on rider data
  const authenticationEmailLogin = AuthenticationHelper.isEmailLogin()
  const isEmailLogin = authenticationEmailLogin === undefined ? Boolean(rider.email) : authenticationEmailLogin
  const loginMethod = isEmailLogin
    ? st.forms.riderProfileDedupe.emailMethod()
    : st.forms.riderProfileDedupe.phoneMethod()
  const loginCredential = isEmailLogin
    ? rider.email
    : phoneUtil.format(phoneUtil.parse(rider.phoneNumber ?? ''), PhoneNumberFormat.NATIONAL)

  return (
    <RiderProfileDedupeView
      firstName={rider.firstName}
      lastName={rider.lastName}
      showWrongAccount={showWrongAccount}
      loginMethod={loginMethod}
      loginCredential={loginCredential || ''}
      organizationName={organization.name}
      onNotMe={() => setShowWrongAccount(true)}
      onContinue={onNextStep}
      onBack={handleBack}
    />
  )
}
